import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { adminGetOrders, adminGetOrder, adminUpdateOrder } from '../../api/orders';
import styles from './OrdersPage.module.css';

const STATUSES = ['all', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];
const STATUS_COLORS: Record<string, string> = {
  pending_payment: '#e65100', confirmed: '#1565c0', processing: '#6a1b9a',
  shipped: '#00838f', delivered: '#2e7d32', cancelled: '#c62828',
};

export default function OrdersPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState<string | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [trackingId, setTrackingId] = useState('');

  const { data } = useQuery({
    queryKey: ['admin-orders', statusFilter],
    queryFn: () => adminGetOrders(statusFilter !== 'all' ? { status: statusFilter } : {}),
  });

  const { data: orderDetail } = useQuery({
    queryKey: ['admin-order', selected],
    queryFn: () => adminGetOrder(selected!),
    enabled: !!selected,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: unknown }) => adminUpdateOrder(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-orders'] });
      qc.invalidateQueries({ queryKey: ['admin-order', selected] });
      toast.success('Order updated');
    },
    onError: () => toast.error('Update failed'),
  });

  return (
    <div className={styles.layout}>
      <div className={styles.listPanel}>
        <h1 className={styles.pageTitle}>{t('admin.orders')}</h1>
        <div className={styles.filters}>
          {STATUSES.map(s => (
            <button key={s} className={`${styles.filterBtn} ${statusFilter === s ? styles.active : ''}`} onClick={() => setStatusFilter(s)}>
              {s === 'all' ? 'All' : s}
            </button>
          ))}
        </div>
        <div className={styles.list}>
          {(data?.orders || []).map((o: Record<string, unknown>) => (
            <div key={o.id as string} className={`${styles.orderItem} ${selected === o.id ? styles.selectedItem : ''}`} onClick={() => setSelected(o.id as string)}>
              <div className={styles.orderItemHeader}>
                <span className={styles.orderId}>#{(o.id as string).slice(0, 8).toUpperCase()}</span>
                <span className={styles.status} style={{ background: STATUS_COLORS[o.status as string] + '20', color: STATUS_COLORS[o.status as string] }}>
                  {o.status as string}
                </span>
              </div>
              <div className={styles.orderItemInfo}>
                <span>{o.userPhone as string}</span>
                <span className={styles.orderTotal}>₹{(o.total as number)?.toLocaleString('en-IN')}</span>
              </div>
              <div className={styles.orderDate}>{new Date(o.createdAt as string).toLocaleDateString('en-IN')}</div>
            </div>
          ))}
        </div>
      </div>

      {selected && orderDetail ? (
        <div className={styles.detailPanel}>
          <h2 className={styles.detailTitle}>Order #{(selected).slice(0, 8).toUpperCase()}</h2>
          <div className={styles.detailSection}>
            <div className={styles.detailRow}><span>Status</span><span className={styles.status} style={{ background: STATUS_COLORS[orderDetail.order?.status] + '20', color: STATUS_COLORS[orderDetail.order?.status] }}>{orderDetail.order?.status}</span></div>
            <div className={styles.detailRow}><span>Total</span><span>₹{orderDetail.order?.total?.toLocaleString('en-IN')}</span></div>
            <div className={styles.detailRow}><span>Phone</span><span>{orderDetail.order?.userPhone}</span></div>
            <div className={styles.detailRow}><span>Payment</span><span>{orderDetail.order?.razorpayPaymentId || '—'}</span></div>
          </div>
          {orderDetail.order?.shippingAddress && (
            <div className={styles.detailSection}>
              <h3>Shipping Address</h3>
              <p>{orderDetail.order.shippingAddress.name}</p>
              <p>{orderDetail.order.shippingAddress.addressLine1}</p>
              {orderDetail.order.shippingAddress.addressLine2 && <p>{orderDetail.order.shippingAddress.addressLine2}</p>}
              <p>{orderDetail.order.shippingAddress.city}, {orderDetail.order.shippingAddress.state} — {orderDetail.order.shippingAddress.pincode}</p>
            </div>
          )}
          <div className={styles.detailSection}>
            <h3>Items</h3>
            {(orderDetail.items || []).map((item: Record<string, unknown>, i: number) => (
              <div key={i} className={styles.detailItem}>
                <span>{item.productName as string} × {item.quantity as number}</span>
                <span>₹{((item.price as number) * (item.quantity as number)).toLocaleString('en-IN')}</span>
              </div>
            ))}
          </div>
          <div className={styles.updateSection}>
            <h3>{t('admin.update_status')}</h3>
            <select value={newStatus} onChange={e => setNewStatus(e.target.value)} className={styles.select}>
              <option value="">Select status...</option>
              {['confirmed', 'processing', 'shipped', 'delivered', 'cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <input placeholder="Tracking ID (optional)" value={trackingId} onChange={e => setTrackingId(e.target.value)} className={styles.input} />
            <button
              className={styles.updateBtn}
              disabled={!newStatus || updateMutation.isPending}
              onClick={() => updateMutation.mutate({ id: selected, payload: { status: newStatus, ...(trackingId && { trackingId }) } })}
            >
              {updateMutation.isPending ? 'Updating...' : 'Update Order'}
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.emptyDetail}><p>Select an order to view details</p></div>
      )}
    </div>
  );
}
